<template>
  <section class="earnings-page">
    <MainNavbar />
    <div class="container">
      <router-link to="/ipromiseafrica/resale/seller-dashboard" class="back-to-dashboard">
        <i class="fa-solid fa-arrow-left"></i>
        Back to Dashboard
      </router-link>
      <!-- Header -->
      <header class="page-header">
        <div class="header-content">
          <h1>
            <i class="fa-solid fa-chart-line"></i>
            Earnings Analytics
          </h1>
          <div class="header-subtitle">
            Track your sales performance and revenue
          </div>
        </div>
        
        <div class="period-selector">
          <select v-model="selectedPeriod" @change="fetchEarningsData" class="period-select">
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="90d">Last 90 Days</option>
            <option value="all">All Time</option>
            <option value="custom">Custom Range</option>
          </select>
          
          <div v-if="selectedPeriod === 'custom'" class="custom-date-range">
            <input type="date" v-model="customStartDate" class="date-input" />
            <span class="date-separator">to</span>
            <input type="date" v-model="customEndDate" class="date-input" />
            <button @click="applyCustomRange" class="apply-btn">
              <i class="fa-solid fa-check"></i>
              Apply
            </button>
          </div>
          
          <button class="refresh-btn" @click="fetchEarningsData" :disabled="loading">
            <i class="fa-solid fa-arrows-rotate" :class="{ 'fa-spin': loading }"></i>
            Refresh
          </button>
        </div>
      </header>

      <!-- Summary Stats -->
      <div v-if="!loading && earningsData" class="summary-stats">
        <div class="stat-card total-revenue">
          <div class="stat-icon">
            <i class="fa-solid fa-money-bill-wave"></i>
          </div>
          <div class="stat-content">
            <div class="stat-value">KES {{ formatCurrency(totalRevenue) }}</div>
            <div class="stat-label">Total Revenue</div>
            <div class="stat-trend" :class="revenueTrendClass">
              <i :class="revenueTrendIcon"></i>
              {{ revenueChangePercentage }}% from previous period
            </div>
          </div>
        </div>
        
        <div class="stat-card items-sold">
          <div class="stat-icon">
            <i class="fa-solid fa-cart-shopping"></i>
          </div>
          <div class="stat-content">
            <div class="stat-value">{{ totalItemsSold }}</div>
            <div class="stat-label">Items Sold</div>
            <div class="stat-trend" :class="salesTrendClass">
              <i :class="salesTrendIcon"></i>
              {{ salesChangePercentage }}% from previous period
            </div>
          </div>
        </div>
        
        <div class="stat-card avg-price">
          <div class="stat-icon">
            <i class="fa-solid fa-tag"></i>
          </div>
          <div class="stat-content">
            <div class="stat-value">KES {{ formatCurrency(averageSellingPrice) }}</div>
            <div class="stat-label">Avg Selling Price</div>
            <div class="stat-subtext">
              vs KES {{ formatCurrency(averageListedPrice) }} listed
            </div>
          </div>
        </div>
        
        <div class="stat-card profit-margin">
          <div class="stat-icon">
            <i class="fa-solid fa-percentage"></i>
          </div>
          <div class="stat-content">
            <div class="stat-value">{{ averagePriceDifferencePercentage }}%</div>
            <div class="stat-label">Avg Price Difference</div>
            <div class="stat-subtext" :class="marginClass">
              <i :class="marginIcon"></i>
              {{ marginLabel }}
            </div>
          </div>
        </div>
      </div>

      <!-- Main Analytics Content -->
      <div v-if="!loading && earningsData" class="analytics-content">
        <!-- Revenue vs Price Difference Chart -->
<!-- Revenue vs Price Difference Chart -->
<div class="chart-container">
  <div class="chart-header">
    <h3>
      <i class="fa-solid fa-chart-column"></i>
      Revenue vs Price Difference
    </h3>
    <div class="chart-legend">
      <span class="legend-item">
        <span class="legend-color revenue"></span>
        Revenue (KES)
      </span>
      <span class="legend-item">
        <span class="legend-color difference"></span>
        Price Difference (%)
      </span>
    </div>
  </div>
  <div class="chart-placeholder">
    <div v-if="chartData.length > 0" class="chart-real">
      <div class="chart-bars">
        <div v-for="item in chartData" :key="item.label" class="chart-bar-container">
          <div class="chart-bar-group">
            <div 
              class="chart-bar revenue-bar" 
              :style="{ height: item.revenue + '%' }"
              :title="'KES ' + formatCurrency(item.revenueValue)"
            >
              <div class="bar-value">{{ formatCurrencyShort(item.revenueValue) }}</div>
            </div>
            <div 
              class="chart-bar difference-bar" 
              :style="{ height: item.difference + '%' }"
              :class="{ positive: getCategoryAvgDifference(item.label) > 0, negative: getCategoryAvgDifference(item.label) < 0 }"
              :title="getCategoryAvgDifference(item.label).toFixed(1) + '%'"
            >
              <div class="bar-value">{{ getCategoryAvgDifference(item.label).toFixed(1) }}%</div>
            </div>
          </div>
          <div class="chart-label">{{ item.label }}</div>
        </div>
      </div>
      <div class="chart-axis">
        <div class="axis-label">Product Categories</div>
        <div class="axis-scale">
          <span>Revenue (KES)</span>
          <span>Price Diff (%)</span>
        </div>
      </div>
    </div>
    
    <div v-else class="no-chart-data">
      <i class="fa-solid fa-chart-bar"></i>
      <p>No data available for chart</p>
    </div>
  </div>
</div>

        <!-- Top Performing Products -->
        <div class="top-products">
          <div class="section-header">
            <h3>
              <i class="fa-solid fa-trophy"></i>
              Top Performing Products
            </h3>
            <router-link to="/seller/listings" class="view-all-link">
              View All Listings
              <i class="fa-solid fa-arrow-right"></i>
            </router-link>
          </div>
          
          <div class="products-grid">
            <div v-for="product in topProducts" :key="product.listing_id" class="product-card">
              <div class="product-image">
                <img
                  :src="product.images && product.images.length ? product.images[product.cover_index || 0] : placeholder"
                  :alt="product.title"
                  class="product-img"
                  loading="lazy"
                  decoding="async"
                />
                <div class="product-badge" :class="getPerformanceBadgeClass(product)">
                  {{ getPerformanceBadgeText(product) }}
                </div>
              </div>
              
              <div class="product-info">
                <h4 class="product-title">{{ product.title }}</h4>
                <div class="product-stats">
                  <div class="price-comparison">
                    <div class="listed-price">
                      <span class="label">Listed:</span>
                      <span class="value">KES {{ formatCurrency(product.price) }}</span>
                    </div>
                    <div class="sold-price">
                      <span class="label">Sold:</span>
                      <span class="value">KES {{ formatCurrency(product.selling_price) }}</span>
                    </div>
                    <div class="price-difference" :class="getPriceDifferenceClass(product)">
                      <i :class="getPriceDifferenceIcon(product)"></i>
                      {{ calculatePriceDifference(product) }}%
                    </div>
                  </div>
                  
                  <div class="product-meta">
                    <span class="meta-item">
                      <i class="fa-solid fa-calendar"></i>
                      {{ formatDate(product.sale_date) }}
                    </span>
                    <span class="meta-item">
                      <i class="fa-solid fa-box"></i>
                      {{ product.quantity_sold }} sold
                    </span>
                    <span class="meta-item">
                      <i class="fa-solid fa-money-bill"></i>
                      KES {{ formatCurrency(product.selling_price * product.quantity_sold) }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Detailed Earnings Breakdown -->
        <div class="earnings-breakdown">
          <div class="section-header">
            <h3>
              <i class="fa-solid fa-list-check"></i>
              Earnings Breakdown
            </h3>
            <button @click="exportToCSV" class="export-btn">
              <i class="fa-solid fa-download"></i>
              Export CSV
            </button>
          </div>
          
          <div class="breakdown-table-container">
            <table class="breakdown-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Category</th>
                  <th>Listed Price</th>
                  <th>Selling Price</th>
                  <th>Quantity</th>
                  <th>Total Revenue</th>
                  <th>Price Difference</th>
                  <th>Sale Date</th>
                  <th>Performance</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in earningsData.sold_items" :key="item.listing_id">
                  <td class="product-cell">
                    <div class="product-name">{{ item.title }}</div>
                  </td>
                  <td>{{ item.category }}</td>
                  <td class="price-cell">
                    KES {{ formatCurrency(item.price) }}
                  </td>
                  <td class="price-cell sold">
                    KES {{ formatCurrency(item.selling_price) }}
                  </td>
                  <td class="quantity-cell">
                    {{ item.quantity_sold }}
                  </td>
                  <td class="revenue-cell">
                    <strong>KES {{ formatCurrency(item.selling_price * item.quantity_sold) }}</strong>
                  </td>
                  <td class="difference-cell" :class="getPriceDifferenceClass(item)">
                    <i :class="getPriceDifferenceIcon(item)"></i>
                    {{ calculatePriceDifference(item) }}%
                  </td>
                  <td class="date-cell">
                    {{ formatDate(item.sale_date) }}
                  </td>
                  <td>
                    <span class="performance-badge" :class="getPerformanceBadgeClass(item)">
                      {{ getPerformanceBadgeText(item) }}
                    </span>
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <td colspan="5" class="summary-label">Total Summary</td>
                  <td class="summary-total">
                    <strong>KES {{ formatCurrency(totalRevenue) }}</strong>
                  </td>
                  <td colspan="3" class="summary-avg">
                    Average Difference: {{ averagePriceDifferencePercentage }}%
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
          
          <div v-if="earningsData.sold_items.length === 0" class="empty-breakdown">
            <i class="fa-solid fa-chart-pie"></i>
            <p>No sales data available for the selected period</p>
          </div>
        </div>

        <!-- Insights Section -->
        <div class="insights-section">
          <h3>
            <i class="fa-solid fa-lightbulb"></i>
            Sales Insights
          </h3>
          
          <div class="insights-grid">
            <div class="insight-card">
              <div class="insight-icon best">
                <i class="fa-solid fa-crown"></i>
              </div>
              <h4>Best Performing Category</h4>
              <div class="insight-content">
                <div class="insight-value">{{ bestCategory.category || 'N/A' }}</div>
                <div class="insight-details">
                  <span>Revenue: KES {{ formatCurrency(bestCategory.revenue) }}</span>
                  <span>Avg Difference: {{ bestCategory.avg_difference }}%</span>
                </div>
              </div>
            </div>
            
            <div class="insight-card">
              <div class="insight-icon worst">
                <i class="fa-solid fa-chart-line-down"></i>
              </div>
              <h4>Lowest Price Difference</h4>
              <div class="insight-content">
                <div class="insight-value">{{ lowestDifference.title || 'N/A' }}</div>
                <div class="insight-details">
                  <span>Difference: {{ lowestDifference.difference }}%</span>
                  <span>Sold at: KES {{ formatCurrency(lowestDifference.selling_price) }}</span>
                </div>
              </div>
            </div>
            
            <div class="insight-card">
              <div class="insight-icon volume">
                <i class="fa-solid fa-fire"></i>
              </div>
              <h4>Highest Volume Sale</h4>
              <div class="insight-content">
                <div class="insight-value">{{ highestVolume.title || 'N/A' }}</div>
                <div class="insight-details">
                  <span>Quantity: {{ highestVolume.quantity }} units</span>
                  <span>Total: KES {{ formatCurrency(highestVolume.revenue) }}</span>
                </div>
              </div>
            </div>
            
            <div class="insight-card">
              <div class="insight-icon premium">
                <i class="fa-solid fa-gem"></i>
              </div>
              <h4>Highest Premium Sale</h4>
              <div class="insight-content">
                <div class="insight-value">{{ highestPremium.title || 'N/A' }}</div>
                <div class="insight-details">
                  <span>Premium: {{ highestPremium.premium }}%</span>
                  <span>Sold for: KES {{ formatCurrency(highestPremium.selling_price) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-if="!loading && (!earningsData || earningsData.sold_items.length === 0)" class="empty-state">
        <div class="empty-illustration">
          <i class="fa-solid fa-chart-line"></i>
        </div>
        <h3>No Sales Data Yet</h3>
        <p>Start selling to see your earnings analytics here.</p>
        <router-link to="/seller/listings" class="empty-action-btn">
          <i class="fa-solid fa-store"></i>
          Go to Listings
        </router-link>
      </div>
    </div>
    <Footer />
  </section>
</template>

<script>
import MainNavbar from "../../components/main/headernfooter/header.vue";
import Footer from "./Footer.vue";
import globalLoadingMixin from "@/mixins/globalLoadingMixin";
import { buildApiUrl } from "@/config/api";

export default {
  name: "SellerEarnings",
  loadingMessage: 'Loading earnings data...',
  mixins: [globalLoadingMixin],
  components: { MainNavbar, Footer },

  data() {
    return {
      loading: false,
      earningsData: null,
      selectedPeriod: '30d',
      customStartDate: '',
      customEndDate: '',
      
      // Will be populated with real data
      chartData: [],
      accountReady: false,
      
      placeholder: "https://via.placeholder.com/300x200?text=No+Image",
    };
  },

  computed: {
    // Summary calculations
    totalRevenue() {
      if (!this.earningsData || !this.earningsData.sold_items) return 0;
      return this.earningsData.sold_items.reduce((total, item) => {
        return total + (item.selling_price * (item.quantity_sold || 1));
      }, 0);
    },

    totalItemsSold() {
      if (!this.earningsData || !this.earningsData.sold_items) return 0;
      return this.earningsData.sold_items.reduce((total, item) => {
        return total + (item.quantity_sold || 1);
      }, 0);
    },

    averageSellingPrice() {
      if (!this.earningsData || !this.earningsData.sold_items || this.totalItemsSold === 0) return 0;
      return this.totalRevenue / this.totalItemsSold;
    },

    averageListedPrice() {
      if (!this.earningsData || !this.earningsData.sold_items || this.earningsData.sold_items.length === 0) return 0;
      const totalListed = this.earningsData.sold_items.reduce((total, item) => {
        return total + item.price;
      }, 0);
      return totalListed / this.earningsData.sold_items.length;
    },

    averagePriceDifferencePercentage() {
      if (!this.earningsData || !this.earningsData.sold_items || this.earningsData.sold_items.length === 0) return 0;
      
      const totalDifference = this.earningsData.sold_items.reduce((total, item) => {
        return total + this.calculatePriceDifference(item);
      }, 0);
      
      return (totalDifference / this.earningsData.sold_items.length).toFixed(1);
    },

    // Updated performance badge logic
    marginClass() {
      return parseFloat(this.averagePriceDifferencePercentage) > 0 ? 'positive' : 
             parseFloat(this.averagePriceDifferencePercentage) < 0 ? 'negative' : 'neutral';
    },

    marginIcon() {
      return parseFloat(this.averagePriceDifferencePercentage) > 0 
        ? 'fa-solid fa-arrow-up' 
        : parseFloat(this.averagePriceDifferencePercentage) < 0 
        ? 'fa-solid fa-arrow-down'
        : 'fa-solid fa-equals';
    },

    marginLabel() {
      const diff = parseFloat(this.averagePriceDifferencePercentage);
      if (diff > 0) return 'Sold above listed price';
      if (diff < 0) return 'Sold below listed price';
      return 'Sold at listed price';
    },

    // Top products
    topProducts() {
      if (!this.earningsData || !this.earningsData.sold_items) return [];
      return [...this.earningsData.sold_items]
        .sort((a, b) => (b.selling_price * b.quantity_sold) - (a.selling_price * a.quantity_sold))
        .slice(0, 4);
    },

    // Insights
    bestCategory() {
      if (!this.earningsData || !this.earningsData.sold_items) return {};
      
      const categories = {};
      this.earningsData.sold_items.forEach(item => {
        if (!categories[item.category]) {
          categories[item.category] = {
            revenue: 0,
            items: 0,
            total_difference: 0
          };
        }
        categories[item.category].revenue += item.selling_price * item.quantity_sold;
        categories[item.category].items += 1;
        categories[item.category].total_difference += this.calculatePriceDifference(item);
      });
      
      let best = { category: '', revenue: 0, avg_difference: 0 };
      Object.entries(categories).forEach(([category, data]) => {
        if (data.revenue > best.revenue) {
          best = {
            category,
            revenue: data.revenue,
            avg_difference: (data.total_difference / data.items).toFixed(1)
          };
        }
      });
      
      return best;
    },

    lowestDifference() {
      if (!this.earningsData || !this.earningsData.sold_items || this.earningsData.sold_items.length === 0) {
        return {};
      }
      
      const sorted = [...this.earningsData.sold_items]
        .sort((a, b) => this.calculatePriceDifference(a) - this.calculatePriceDifference(b));
      
      const item = sorted[0];
      return {
        title: item.title,
        difference: this.calculatePriceDifference(item).toFixed(1),
        selling_price: item.selling_price
      };
    },

    highestVolume() {
      if (!this.earningsData || !this.earningsData.sold_items || this.earningsData.sold_items.length === 0) {
        return {};
      }
      
      const sorted = [...this.earningsData.sold_items]
        .sort((a, b) => b.quantity_sold - a.quantity_sold);
      
      const item = sorted[0];
      return {
        title: item.title,
        quantity: item.quantity_sold,
        revenue: item.selling_price * item.quantity_sold
      };
    },

    highestPremium() {
      if (!this.earningsData || !this.earningsData.sold_items || this.earningsData.sold_items.length === 0) {
        return {};
      }
      
      const sorted = [...this.earningsData.sold_items]
        .sort((a, b) => this.calculatePriceDifference(b) - this.calculatePriceDifference(a));
      
      const item = sorted[0];
      return {
        title: item.title,
        premium: this.calculatePriceDifference(item).toFixed(1),
        selling_price: item.selling_price
      };
    }
  },

  methods: {
    async ensureSellerAccountAccess() {
      const token = localStorage.getItem("access_token") || localStorage.getItem("seller_token");
      if (!token) {
        this.$router.push("/ipromiseafrica/sign-in");
        return false;
      }
      try {
        const res = await fetch(buildApiUrl("seller/me"), {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (res.ok) return true;
        if (res.status === 401) {
          localStorage.removeItem("access_token");
          localStorage.removeItem("seller_token");
          this.$router.push("/ipromiseafrica/sign-in");
          return false;
        }

        let detail = "";
        try {
          const err = await res.json();
          detail = String(err?.detail || "").toLowerCase();
        } catch (e) {
          detail = "";
        }
        if (detail.includes("suspended")) {
          localStorage.removeItem("access_token");
          localStorage.removeItem("seller_token");
          this.$router.push("/ipromiseafrica/sign-in");
          return false;
        }
        return true;
      } catch (error) {
        return true;
      }
    },

    handleAuthError(res) {
      if (res && (res.status === 401 || res.status === 403)) {
        localStorage.removeItem("access_token");
        alert("Your session has expired. Please sign in again.");
        this.$router.push("/ipromiseafrica/sign-in");
        return true;
      }
      return false;
    },

    async fetchEarningsData() {
      try {
        this.loading = true;
        const token = localStorage.getItem("access_token");

        if (!token) {
          alert("You must be logged in to view earnings.");
          return;
        }

        // Build query parameters
        const params = new URLSearchParams();
        params.append('period', this.selectedPeriod);
        
        if (this.selectedPeriod === 'custom' && this.customStartDate && this.customEndDate) {
          params.append('start_date', this.customStartDate);
          params.append('end_date', this.customEndDate);
        }

        const res = await fetch(buildApiUrl(`seller-earning/earnings?${params.toString()}`), {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (this.handleAuthError(res)) return;
        if (!res.ok) {
          if (res.status === 404) {
            // No data available yet
            this.earningsData = { sold_items: [] };
            return;
          }
          throw new Error("Failed to load earnings data");
        }

        const data = await res.json();
        const soldItems = Array.isArray(data?.sold_items) ? data.sold_items : [];
        this.earningsData = {
          ...data,
          sold_items: soldItems.map((item) => ({
            ...item,
            quantity_sold: item.quantity_sold || 1,
            selling_price: Number(item.selling_price || 0),
            price: Number(item.price || 0),
          })),
        };
        
        // Generate real chart data from response
        this.generateChartData();

      } catch (error) {
        console.error("Fetch earnings error:", error);
        alert("Could not load earnings data. Please try again.");
      } finally {
        this.loading = false;
      }
    },

    generateChartData() {
      if (!this.earningsData || !this.earningsData.sold_items || this.earningsData.sold_items.length === 0) {
        this.chartData = [];
        return;
      }

      // Group by category
      const categories = {};
      this.earningsData.sold_items.forEach(item => {
        if (!categories[item.category]) {
          categories[item.category] = {
            revenue: 0,
            items: 0,
            totalPriceDiff: 0
          };
        }
        categories[item.category].revenue += item.selling_price * item.quantity_sold;
        categories[item.category].items += 1;
        categories[item.category].totalPriceDiff += this.calculatePriceDifference(item);
      });

      // Convert to chart format
      const chartItems = [];
      let maxRevenue = 0;
      let maxPriceDiff = 0;

      Object.entries(categories).forEach(([category, data]) => {
        const avgPriceDiff = data.items > 0 ? data.totalPriceDiff / data.items : 0;
        chartItems.push({
          label: category,
          revenue: data.revenue,
          revenueValue: data.revenue,
          difference: avgPriceDiff
        });

        maxRevenue = Math.max(maxRevenue, data.revenue);
        maxPriceDiff = Math.max(maxPriceDiff, Math.abs(avgPriceDiff));
      });

      // Normalize for chart display (scale to percentage)
      this.chartData = chartItems.map(item => ({
        label: item.label,
        revenue: maxRevenue > 0 ? (item.revenue / maxRevenue) * 80 : 0, // Scale to 80% max height
        revenueValue: item.revenue,
        difference: maxPriceDiff > 0 ? (Math.abs(item.difference) / maxPriceDiff) * 80 : 0 // Scale to 80% max height
      }));
    },

    applyCustomRange() {
      if (!this.customStartDate || !this.customEndDate) {
        alert("Please select both start and end dates");
        return;
      }
      
      if (new Date(this.customStartDate) > new Date(this.customEndDate)) {
        alert("Start date must be before end date");
        return;
      }
      
      this.fetchEarningsData();
    },

    calculatePriceDifference(item) {
      if (!item || !item.price || !item.selling_price) return 0;
      const difference = ((item.selling_price - item.price) / item.price) * 100;
      return Math.round(difference * 10) / 10; // Round to 1 decimal
    },

    getPriceDifferenceClass(item) {
      const diff = this.calculatePriceDifference(item);
      if (diff > 0) return 'positive';
      if (diff < 0) return 'negative';
      return 'neutral';
    },

    getPriceDifferenceIcon(item) {
      const diff = this.calculatePriceDifference(item);
      if (diff > 0) return 'fa-solid fa-arrow-up';
      if (diff < 0) return 'fa-solid fa-arrow-down';
      return 'fa-solid fa-equals';
    },

    // Updated performance badge logic based on price difference only
    getPerformanceBadgeClass(item) {
      const diff = this.calculatePriceDifference(item);
      
      if (diff > 0) return 'excellent';  // Sold above quoted price = Best Deal
      if (diff < 0) return 'poor';      // Sold below quoted price = Below Expectation
      return 'good';                    // Sold at quoted price = Good Deal
    },

    getPerformanceBadgeText(item) {
      const diff = this.calculatePriceDifference(item);
      
      if (diff > 0) return 'Best Deal';
      if (diff < 0) return 'Below Expectation';
      return 'Good Deal';
    },

    formatCurrency(amount) {
      if (!amount) return '0';
      return parseFloat(amount).toLocaleString('en-KE', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      });
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-KE', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },
      getCategoryAvgDifference(category) {
    if (!this.earningsData || !this.earningsData.sold_items) return 0;
    
    const categoryItems = this.earningsData.sold_items.filter(item => item.category === category);
    if (categoryItems.length === 0) return 0;
    
    const totalDiff = categoryItems.reduce((sum, item) => sum + this.calculatePriceDifference(item), 0);
    return totalDiff / categoryItems.length;
  },

  formatCurrencyShort(amount) {
    if (!amount) return '0';
    
    if (amount >= 1000000) {
      return 'KES ' + (amount / 1000000).toFixed(1) + 'M';
    } else if (amount >= 1000) {
      return 'KES ' + (amount / 1000).toFixed(0) + 'K';
    } else {
      return 'KES ' + this.formatCurrency(amount);
    }
  },

    exportToCSV() {
      if (!this.earningsData || !this.earningsData.sold_items.length) {
        alert("No data to export");
        return;
      }

      // Create CSV headers
      const headers = [
        'Product Title',
        'Category',
        'Listed Price',
        'Selling Price',
        'Quantity Sold',
        'Total Revenue',
        'Price Difference %',
        'Sale Date',
        'Performance'
      ];

      // Create CSV rows
      const rows = this.earningsData.sold_items.map(item => [
        `"${item.title}"`,
        item.category,
        item.price,
        item.selling_price,
        item.quantity_sold || 1,
        item.selling_price * (item.quantity_sold || 1),
        this.calculatePriceDifference(item),
        this.formatDate(item.sale_date),
        this.getPerformanceBadgeText(item)
      ]);

      // Add summary row
      rows.push([
        'TOTAL',
        '',
        '',
        '',
        this.totalItemsSold,
        this.totalRevenue,
        this.averagePriceDifferencePercentage + '%',
        '',
        'AVERAGE'
      ]);

      // Combine headers and rows
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.join(','))
      ].join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `earnings_${this.selectedPeriod}_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    }
  },

  async beforeMount() {
    await this.ensureSellerAccountAccess();
  },

  mounted() {
    // Set default custom dates (last 30 days)
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 30);
    
    this.customEndDate = endDate.toISOString().split('T')[0];
    this.customStartDate = startDate.toISOString().split('T')[0];
    
    this.fetchEarningsData();
  }
};
</script>

<style scoped>
.back-to-dashboard {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-color1);
  text-decoration: none;
  font-size: 0.95rem;
  margin-bottom: 1rem;
  padding: 0.5rem 0;
}
.back-to-dashboard:hover {
  color: var(--border-color-hover);
}

.earnings-page {
  background: var(--dashboard-bg);
  min-height: 100vh;
  color: var(--text-color1);
  font-family: "Inter", sans-serif;
}

.container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 100px 24px 40px;
}

/* Header */
.page-header {
  background: var(--card-bg);
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 32px;
  box-shadow: 0 2px 16px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.header-content h1 {
  margin: 0 0 8px 0;
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--heading-color);
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-content h1 i {
  color: var(--border-color-hover);
}

.header-subtitle {
  color: var(--subtext-color);
  font-size: 14px;
}

.period-selector {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 20px;
  flex-wrap: wrap;
}

.period-select {
  padding: 10px 16px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: var(--background-color2);
  color: var(--text-color1);
  font-size: 14px;
  cursor: pointer;
  min-width: 160px;
}

.custom-date-range {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.date-input {
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: var(--background-color2);
  color: var(--text-color1);
  font-size: 14px;
}

.date-separator {
  color: var(--subtext-color);
  font-size: 14px;
}

.apply-btn {
  padding: 10px 16px;
  background: rgba(0, 185, 255, 0.1);
  color: var(--border-color-hover);
  border: 1px solid rgba(0, 185, 255, 0.2);
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  transition: all 0.2s;
}

.apply-btn:hover {
  background: rgba(0, 185, 255, 0.2);
}

.refresh-btn {
  padding: 10px 20px;
  background: var(--border-color-hover);
  color: var(--text-color3);
  border: none;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;
  margin-left: auto;
}

.refresh-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 185, 255, 0.3);
}

.refresh-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Summary Stats */
.summary-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 32px;
}

.stat-card {
  background: var(--card-bg);
  border-radius: 16px;
  padding: 24px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 20px;
}

.stat-card:hover {
  transform: translateY(-4px);
  border-color: var(--border-color-hover);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
}

.stat-icon {
  width: 60px;
  height: 60px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
}

.total-revenue .stat-icon {
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
}

.items-sold .stat-icon {
  background: linear-gradient(135deg, #3b82f6, #1d4ed8);
  color: white;
}

.avg-price .stat-icon {
  background: linear-gradient(135deg, #8b5cf6, #7c3aed);
  color: white;
}

.profit-margin .stat-icon {
  background: linear-gradient(135deg, #f59e0b, #d97706);
  color: white;
}

.stat-content {
  flex: 1;
}

.stat-value {
  font-size: 24px;
  font-weight: 800;
  margin-bottom: 4px;
  color: var(--heading-color);
}

.stat-label {
  font-size: 14px;
  color: var(--subtext-color);
  margin-bottom: 8px;
}

.stat-trend {
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.stat-trend.positive {
  color: #10b981;
}

.stat-trend.negative {
  color: #ef4444;
}

.stat-subtext {
  font-size: 12px;
  color: var(--subtext-color);
  display: flex;
  align-items: center;
  gap: 4px;
}

.stat-subtext.positive {
  color: #10b981;
}

.stat-subtext.negative {
  color: #ef4444;
}

/* Chart Container */
.chart-container {
  background: var(--card-bg);
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 32px;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
}

.chart-header h3 {
  margin: 0;
  font-size: 18px;
  color: var(--heading-color);
  display: flex;
  align-items: center;
  gap: 10px;
}
.chart-real {
  width: 100%;
  height: 100%;
  position: relative;
}

.chart-placeholder {
  width: 100%;
  overflow-x: auto;
}

.chart-bars {
  display: flex;
  justify-content: space-around;
  align-items: flex-end;
  height: 80%;
  padding: 0 40px;
  min-width: 520px;
}

.chart-bar-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  flex: 1;
  max-width: 100px;
}

.chart-bar-group {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  height: 100%;
  width: 100%;
  position: relative;
}

.chart-bar {
  border-radius: 4px 4px 0 0;
  width: 45%;
  transition: all 0.3s ease;
  position: relative;
  min-height: 20px;
}

.revenue-bar {
  background: var(--border-color-hover);
  opacity: 0.9;
}

.difference-bar {
  opacity: 0.7;
}

.difference-bar.positive {
  background: #10b981;
}

.difference-bar.negative {
  background: #ef4444;
}

.bar-value {
  position: absolute;
  top: -25px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 11px;
  color: var(--text-color1);
  white-space: nowrap;
  font-weight: 600;
  background: rgba(0, 0, 0, 0.7);
  padding: 2px 6px;
  border-radius: 4px;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.chart-bar:hover .bar-value {
  opacity: 1;
}

.chart-label {
  font-size: 12px;
  color: var(--subtext-color);
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
}

.chart-axis {
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: 20px;
  margin-top: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.axis-label {
  color: var(--subtext-color);
  font-size: 13px;
}

.axis-scale {
  display: flex;
  gap: 20px;
  font-size: 11px;
  color: var(--subtext-color);
}

.no-chart-data {
  text-align: center;
  padding: 40px;
  color: var(--subtext-color);
}

.no-chart-data i {
  font-size: 48px;
  margin-bottom: 16px;
  color: rgba(0, 185, 255, 0.3);
}

.no-chart-data p {
  margin: 0;
  font-size: 14px;
}
.chart-legend {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: var(--subtext-color);
}

.legend-color {
  width: 12px;
  height: 12px;
  border-radius: 2px;
}

.legend-color.revenue {
  background: var(--border-color-hover);
}

.legend-color.difference {
  background: #10b981;
}

.chart-placeholder {
  height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.chart-mock {
  width: 100%;
  height: 100%;
  position: relative;
}

.mock-bars {
  display: flex;
  justify-content: space-around;
  align-items: flex-end;
  height: 80%;
  padding: 0 40px;
}

.mock-bar-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  flex: 1;
  max-width: 80px;
}

.mock-bar-group {
  display: flex;
  align-items: flex-end;
  gap: 4px;
  height: 100%;
  width: 100%;
}

.mock-bar {
  border-radius: 4px 4px 0 0;
  width: 50%;
  transition: all 0.3s ease;
}

.mock-bar:hover {
  opacity: 0.8;
}

.revenue-bar {
  background: var(--border-color-hover);
  opacity: 0.9;
}

.difference-bar {
  opacity: 0.7;
}

.difference-bar.positive {
  background: #10b981;
}

.difference-bar.negative {
  background: #ef4444;
}

.mock-label {
  font-size: 12px;
  color: var(--subtext-color);
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
}

.mock-axis {
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: 20px;
  margin-top: 20px;
}

.axis-label {
  text-align: center;
  color: var(--subtext-color);
  font-size: 13px;
}

/* Top Products */
.top-products {
  background: var(--card-bg);
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 32px;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
}

.section-header h3 {
  margin: 0;
  font-size: 18px;
  color: var(--heading-color);
  display: flex;
  align-items: center;
  gap: 10px;
}

.view-all-link {
  color: var(--border-color-hover);
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;
}

.view-all-link:hover {
  gap: 10px;
}

.products-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
}

.product-card {
  background: rgba(255, 255, 255, 0.02);
  border-radius: 12px;
  padding: 20px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  transition: all 0.3s ease;
}

.product-card:hover {
  border-color: rgba(0, 185, 255, 0.2);
  transform: translateY(-4px);
}

.product-image {
  position: relative;
  height: 160px;
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 16px;
}

.product-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.product-badge {
  position: absolute;
  top: 12px;
  right: 12px;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.product-badge.excellent {
  background: rgba(16, 185, 129, 0.9);
  color: white;
}

.product-badge.good {
  background: rgba(59, 130, 246, 0.9);
  color: white;
}

.product-badge.average {
  background: rgba(156, 163, 175, 0.9);
  color: white;
}

.product-badge.poor {
  background: rgba(239, 68, 68, 0.9);
  color: white;
}

.product-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--heading-color);
  margin: 0 0 16px 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.price-comparison {
  background: rgba(255, 255, 255, 0.03);
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 12px;
}

.listed-price, .sold-price {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  font-size: 13px;
}

.listed-price .label, .sold-price .label {
  color: var(--subtext-color);
}

.listed-price .value {
  color: var(--text-color1);
}

.sold-price .value {
  color: var(--border-color-hover);
  font-weight: 600;
}

.price-difference {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 8px;
  border-radius: 6px;
  margin-top: 8px;
}

.price-difference.positive {
  background: rgba(16, 185, 129, 0.1);
  color: #10b981;
}

.price-difference.negative {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}

.price-difference.neutral {
  background: rgba(156, 163, 175, 0.1);
  color: var(--subtext-color);
}

.product-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  font-size: 12px;
  color: var(--subtext-color);
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 4px;
}

/* Earnings Breakdown */
.earnings-breakdown {
  background: var(--card-bg);
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 32px;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.export-btn {
  padding: 10px 20px;
  background: rgba(0, 185, 255, 0.1);
  color: var(--border-color-hover);
  border: 1px solid rgba(0, 185, 255, 0.2);
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  transition: all 0.2s;
}

.export-btn:hover {
  background: rgba(0, 185, 255, 0.2);
}

.breakdown-table-container {
  overflow-x: auto;
  margin-top: 20px;
}

.breakdown-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.breakdown-table th {
  text-align: left;
  padding: 16px 12px;
  background: rgba(255, 255, 255, 0.03);
  color: var(--subtext-color);
  font-weight: 600;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  white-space: nowrap;
}

.breakdown-table td {
  padding: 16px 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  vertical-align: middle;
}

.breakdown-table tbody tr:hover {
  background: rgba(255, 255, 255, 0.02);
}

.product-name {
  font-weight: 500;
  color: var(--heading-color);
}

.price-cell {
  font-family: 'Courier New', monospace;
}

.price-cell.sold {
  color: var(--border-color-hover);
  font-weight: 600;
}

.quantity-cell {
  text-align: center;
  font-weight: 600;
}

.revenue-cell {
  font-family: 'Courier New', monospace;
  color: var(--heading-color);
}

.difference-cell {
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
}

.difference-cell.positive {
  color: #10b981;
}

.difference-cell.negative {
  color: #ef4444;
}

.date-cell {
  color: var(--subtext-color);
  white-space: nowrap;
}

.performance-badge {
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  display: inline-block;
}

.performance-badge.excellent {
  background: rgba(16, 185, 129, 0.1);
  color: #10b981;
}

.performance-badge.good {
  background: rgba(59, 130, 246, 0.1);
  color: #3b82f6;
}

.performance-badge.average {
  background: rgba(156, 163, 175, 0.1);
  color: var(--subtext-color);
}

.performance-badge.poor {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}

.breakdown-table tfoot {
  background: rgba(255, 255, 255, 0.03);
}

.breakdown-table tfoot td {
  padding: 20px 12px;
  font-weight: 600;
  border-top: 2px solid rgba(255, 255, 255, 0.1);
}

.summary-label {
  color: var(--heading-color);
  font-size: 16px;
}

.summary-total {
  font-size: 18px;
  color: var(--border-color-hover);
}

.summary-avg {
  text-align: right;
  color: var(--subtext-color);
}

.empty-breakdown {
  text-align: center;
  padding: 60px 20px;
  color: var(--subtext-color);
}

.empty-breakdown i {
  font-size: 48px;
  margin-bottom: 16px;
  color: rgba(0, 185, 255, 0.3);
}

.empty-breakdown p {
  margin: 0;
  font-size: 16px;
}

/* Insights Section */
.insights-section {
  background: var(--card-bg);
  border-radius: 16px;
  padding: 24px;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.insights-section h3 {
  margin: 0 0 24px 0;
  font-size: 18px;
  color: var(--heading-color);
  display: flex;
  align-items: center;
  gap: 10px;
}

.insights-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
}

.insight-card {
  background: rgba(255, 255, 255, 0.02);
  border-radius: 12px;
  padding: 20px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  transition: all 0.3s ease;
}

.insight-card:hover {
  border-color: rgba(0, 185, 255, 0.2);
  transform: translateY(-4px);
}

.insight-icon {
  width: 50px;
  height: 50px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  margin-bottom: 16px;
}

.insight-icon.best {
  background: linear-gradient(135deg, #f59e0b, #d97706);
  color: white;
}

.insight-icon.worst {
  background: linear-gradient(135deg, #ef4444, #dc2626);
  color: white;
}

.insight-icon.volume {
  background: linear-gradient(135deg, #8b5cf6, #7c3aed);
  color: white;
}

.insight-icon.premium {
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
}

.insight-card h4 {
  margin: 0 0 12px 0;
  font-size: 16px;
  color: var(--heading-color);
}

.insight-value {
  font-size: 18px;
  font-weight: 700;
  color: var(--heading-color);
  margin-bottom: 8px;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.insight-details {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 13px;
  color: var(--subtext-color);
}

/* Loading State */
.loading-state {
  text-align: center;
  padding: 80px 20px;
}

.loading-spinner {
  width: 50px;
  height: 50px;
  border: 3px solid var(--loader-border);
  border-top-color: var(--loader-color);
  border-radius: 50%;
  margin: 0 auto 20px;
  animation: spin 1s linear infinite;
}

.loading-state p {
  color: var(--subtext-color);
  font-size: 16px;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 80px 20px;
  background: var(--card-bg);
  border-radius: 16px;
  border: 2px dashed rgba(255, 255, 255, 0.1);
  margin: 40px 0;
}

.empty-illustration {
  width: 100px;
  height: 100px;
  margin: 0 auto 24px;
  background: rgba(0, 185, 255, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.empty-illustration i {
  font-size: 48px;
  color: var(--border-color-hover);
}

.empty-state h3 {
  font-size: 24px;
  margin: 0 0 16px 0;
  color: var(--heading-color);
}

.empty-state p {
  color: var(--subtext-color);
  max-width: 500px;
  margin: 0 auto 32px;
  line-height: 1.6;
  font-size: 16px;
}

.empty-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 14px 28px;
  background: var(--border-color-hover);
  color: var(--text-color3);
  text-decoration: none;
  border-radius: 12px;
  font-weight: 600;
  transition: all 0.3s ease;
}

.empty-action-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 185, 255, 0.3);
}

/* Animations */
@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Responsive Design */
@media (max-width: 768px) {
  .container {
    padding: 80px 16px 32px;
  }
  
  .period-selector {
    flex-direction: column;
    align-items: stretch;
  }
  
  .custom-date-range {
    flex-direction: column;
    align-items: stretch;
  }
  
  .date-input {
    width: 100%;
  }
  
  .refresh-btn {
    margin-left: 0;
    width: 100%;
    justify-content: center;
  }
  
  .summary-stats {
    grid-template-columns: 1fr;
  }
  
  .chart-header, .section-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .chart-bars {
    min-width: 680px;
    padding: 0 16px;
  }
  
  .breakdown-table {
    font-size: 12px;
  }
  
  .breakdown-table th,
  .breakdown-table td {
    padding: 12px 8px;
  }
  
  .insights-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 480px) {
  .chart-placeholder {
    height: 250px;
  }
  
  .mock-bars {
    padding: 0 20px;
  }
  
  .mock-label {
    font-size: 10px;
  }
  
  .products-grid {
    grid-template-columns: 1fr;
  }
}
</style>